import React, { useCallback, useEffect, useState, useRef } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
// Actions
import {
  searchAccountApiKeys,
  openAccountApiKeyCreateEditModal,
  openAccountApiKeyDeleteModal,
  clearRedux
} from "Redux/actions";
// Components
import { NotificationManager } from 'Components/Notifications';
import { Grid, Grow, Container, Icon, Typography } from "@mui/material";
import { Table, SearchInput, Toolbar, Button } from 'Components/uiControls';
import { MenuComponent } from 'Components/Menu';
import { useNextTokenInfiniteScroll } from 'Components/uiControls/Table/hooks';
import CreateEditAccountApiKey from './components/createEdit';
import DeleteAccountApiKey from './components/delete';

import { Pill, IconButton } from '@gpstrackit/gtc-react-controls';
import { copyToClipboard } from 'Core/data/Helpers';
import './styles.scss';
import { HasPermission } from "Core/security"

const ApiKeysTab = (props) => {
  // Props
  const {
    intl: { messages },
    showTable
  } = props;

  // Hooks
  const dispatch = useDispatch();

  //Stale local 
  const {
    loadingApiKeys,
    apiKeys,
    filters,
    errorApiKeys,
    openCreateEdit,
    openDelete,
    deleteResponse: response,
    updateSubUsers
  } = useSelector((state) => state.apiKeysRedux);
  const user = useSelector(state => state.securityRedux.user);

  // Permissions
  const editablePermissions = HasPermission(user, "", [335]);

  //State manager
  const apiKeysTableRef = useRef();
  const { nextToken } = filters;
  const infiniteNextTokenScroll = useNextTokenInfiniteScroll(apiKeysTableRef, searchAccountApiKeys, filters, nextToken);

  useEffect(() => {
    return () => {
      dispatch(clearRedux("ACCOUNT_API_KEYS"));
    }
  }, []);

  useEffect(() => {
    infiniteNextTokenScroll.setColumns([
      {
        key: 'name',
        title: messages['accountApiKeys_name'],
        show: true,
        render: (row) => {
          return (
            <div className="context-menu-container" style={{ display: "flex", flexDirection: "row", alignItems: "center" }} >
              <div>
                <Typography noWrap>{row.name}</Typography>
              </div>
              {editablePermissions &&
                <div>
                  <MenuComponent
                    disabled={loadingApiKeys}
                    options={[
                      messages['edit'],
                      messages['delete']
                    ]}
                    icon={'more_vert'}
                    className="context-menu"
                    onClick={(type, value) => {
                      if (type === messages['edit']) {
                        dispatch(openAccountApiKeyCreateEditModal(true, value?.id));
                      } else if (type === messages['delete']) {
                        dispatch(openAccountApiKeyDeleteModal(true, [value]));
                      }
                    }}
                    value={row}
                  />
                </div>
              }
            </div>
          )
        }
      },
      {
        key: 'key',
        title: messages['accountApiKeys_apiKey'],
        show: true,
        render: (row) => {
          const maskedKey = row.key ? `••••••••${row.key.slice(-4)}` : '';
          return (
            <div className="context-menu-container" style={{ display: "flex", flexDirection: "row", alignItems: "center" }} >
              <div>
                <Typography noWrap>{maskedKey}</Typography>
              </div>
              {editablePermissions &&
                <div>
                  <IconButton
                    disabled={loadingApiKeys}
                    onClick={() => copyToClipboard(row.key)}
                    className="icon"
                    icon="content_copy"
                    size="xsmall"
                  />
                </div>
              }
            </div>
          )
        }
      },
      {
        key: 'assignedToUser',
        title: messages['accountApiKeys_assignedToUser'],
        show: true,
        render: (row) => {
          return (
            <div style={{ display: "flex", flexDirection: "column" }} >
              {row?.user?.firstName && <div><label>{`${row.user?.firstName} ${row.user?.lastName}`} </label></div>}
              {row?.user?.userName && <div><label style={{ color: '#8c8c8c' }}>{`${row.user?.userName}`}</label></div>}
            </div>
          )
        }
      }
    ]);
    infiniteNextTokenScroll.onChangePage();
  }, []);

  useEffect(() => {
    if (errorApiKeys) {
      NotificationManager.error(
        messages['errorMessage'],
        messages['error'],
        3000,
        null,
        null,
        'warning',
      );
      dispatch(clearRedux("ACCOUNT_API_KEYS"));
    }
  }, [errorApiKeys]);

  useEffect(() => {
    if (response) {
      if (response.error) {
        NotificationManager.error(
          messages['errorMessage'],
          messages['error'],
          3000,
          null,
          null,
          'warning',
        );
      } else {
        NotificationManager.success(
          messages['accountApiKeys_deleted'],
          messages['success'],
          3000,
          null,
          null,
          'success',
        );
      }
    }
  }, [response]);

  useEffect(() => {
    if (updateSubUsers) {
      if (apiKeys?.length) {
        getData("", true);
      }
      dispatch(clearRedux("ACCOUNT_API_KEYS", { updateSubUsers: false }));
    }
  }, [updateSubUsers])

  const getData = useCallback((openSearch = "", resetSearch = false) => {
    dispatch(searchAccountApiKeys({ ...filters, openSearch: openSearch, resetSearch: openSearch || resetSearch || false, nextToken: null }));
    infiniteNextTokenScroll.setSearchText(openSearch);
    apiKeysTableRef?.current?.setPage(1);
    apiKeysTableRef?.current?.setSelectedItems([]);
  });

  return (
    <Container maxWidth={false}>
      <Grid container direction="row" justifyContent="center" alignItems="center">
        <Grid item sm={4} style={{ display: "flex" }}>
          <Pill key={'pillKey1'} item={{ id: 1, label: messages["permissions_apiKeys"], color: "default-green" }} variant={"filled"} onClick={() => showTable("apiKeys")} />
          {
            HasPermission(user, ["wbhook"], [264]) &&
            <Pill key={'pillKey2'} item={{ id: 2, label: messages["accountWebHooks_webhooks"], color: "default" }} variant={"filled"} onClick={() => showTable("webHooks")} />
          }
        </Grid>
      </Grid>
      <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
        <Toolbar>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {editablePermissions &&
              <>
                <Grid item>
                  <div style={{ marginLeft: 6 }}>
                    <Button
                      type="icon"
                      icon="add_box"
                      placement="bottom"
                      tooltip={messages['add']}
                      onClick={(e) => {
                        if (e) e?.preventDefault();
                        dispatch(openAccountApiKeyCreateEditModal(true, null));
                      }}
                    />
                  </div>
                </Grid>
                <Grid item>
                  <Button
                    type="icon"
                    icon="delete"
                    placement="bottom"
                    tooltip={messages['delete']}
                    disabled={!infiniteNextTokenScroll?.selectedItems?.length}
                    onClick={(e) => {
                      dispatch(openAccountApiKeyDeleteModal(true, infiniteNextTokenScroll?.selectedItems));
                    }}
                  />
                </Grid>
              </>
            }
            <Grid item>
              <a href="https://api-docs.gpstrackit.com" target="_blank" className="api-keys-documentation-link">
                <div className="api-keys-documentation">
                  <span className="title">{messages['documentation']}</span>
                  <Icon className="icon">open_in_new</Icon>
                </div>
              </a>
            </Grid>
          </Grid>
        </Toolbar>
        <Grid item xs={12} sm={6} className="mb-10 text-right">
          <SearchInput
            placeholder={messages['search']}
            {...infiniteNextTokenScroll}
          />
        </Grid>
      </Grid>

      <Table
        ref={apiKeysTableRef}
        keyColumn="id"
        loading={loadingApiKeys}
        topheight={235}
        data={apiKeys}
        exportOrientation='landscape'
        exportName='account-api-keys'
        noMoreItemsMessage={messages['noMoreItems']}
        {...infiniteNextTokenScroll}
      />

      {
        openCreateEdit && <CreateEditAccountApiKey callback={getData} />
      }

      {
        openDelete && <DeleteAccountApiKey callback={getData} />
      }
    </Container>
  );
}

export default injectIntl(ApiKeysTab);